import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { portalAPI } from '../../api';
import Maintenance from './Maintenance';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Textarea } from '../../components/ui/Textarea';
import { FormLabel } from '../../components/ui/FormLabel';
import { cn } from '../../lib/utils';
import { sanitizeRichTextHtml } from '../../lib/sanitizeRichText';
import { useToast } from '../../hooks/useToast';
import {
  PORTAL_QUERY_REASON_LABELS,
  PORTAL_REJECT_TAG_LABELS,
} from '../../lib/estimateClientPortalFeedback';
import { X } from 'lucide-react';

const PublicEstimateView = () => {
  const { token } = useParams();
  const { success, error: toastError, ToastContainer } = useToast();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [estimate, setEstimate] = useState(null);
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectTags, setRejectTags] = useState(() => new Set());
  const [rejectComment, setRejectComment] = useState('');
  const [queryOpen, setQueryOpen] = useState(false);
  const [queryReason, setQueryReason] = useState('price');
  const [queryDetails, setQueryDetails] = useState('');

  useEffect(() => {
    fetchEstimate();
  }, [token]);

  const fetchEstimate = async () => {
    try {
      setLoading(true);
      setError('');
      setIsMaintenanceMode(false);
      const data = await portalAPI.getEstimateByToken(token);
      setEstimate(data);
    } catch (err) {
      // Check if this is a maintenance mode error
      if (err.response?.status === 503 && err.response?.data?.maintenance) {
        setIsMaintenanceMode(true);
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load estimate');
      }
      console.error('Error fetching estimate:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getClientName = () => {
    if (!estimate?.client) return '';
    if (estimate.client.clientType === 'company') {
      return estimate.client.companyName || 'Unnamed Company';
    }
    return `${estimate.client.firstName || ''} ${estimate.client.lastName || ''}`.trim() || 'Unnamed Individual';
  };

  const getStatusVariant = (status) => {
    const variants = {
      draft: 'neutral',
      sent: 'neutral',
      accepted: 'success',
      rejected: 'danger',
      queried: 'warning',
      converted: 'neutral',
    };
    return variants[status] || 'neutral';
  };

  const handleAccept = async () => {
    if (!token || actionLoading) return;
    try {
      setActionLoading('accept');
      const res = await portalAPI.acceptEstimateByToken(token);
      const nextStatus = res?.data?.status;
      if (nextStatus) {
        setEstimate((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      }
      success('Estimate accepted', 'Thank you. The business has been notified.');
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.message || err.message || 'Something went wrong';
      toastError('Could not accept', msg);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleRejectTag = (key) => {
    setRejectTags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const submitReject = async () => {
    if (!token || actionLoading) return;
    const reasonTags = [...rejectTags];
    const comment = rejectComment.trim();
    if (reasonTags.length === 0 && !comment) {
      toastError('Add a reason', 'Please choose at least one option or add a comment.');
      return;
    }
    try {
      setActionLoading('reject');
      const res = await portalAPI.rejectEstimateByToken(token, { reasonTags, comment });
      const nextStatus = res?.data?.status;
      if (nextStatus) {
        setEstimate((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      }
      setRejectOpen(false);
      setRejectTags(new Set());
      setRejectComment('');
      success('Estimate declined', 'Your response has been recorded.');
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.message || err.message || 'Something went wrong';
      toastError('Could not decline', msg);
    } finally {
      setActionLoading(null);
    }
  };

  const submitQuery = async () => {
    if (!token || actionLoading) return;
    if (!queryReason) {
      toastError('Reason required', 'Please select a reason for your query.');
      return;
    }
    try {
      setActionLoading('query');
      const res = await portalAPI.queryEstimateByToken(token, {
        queryReason,
        details: queryDetails.trim(),
      });
      const nextStatus = res?.data?.status;
      if (nextStatus) {
        setEstimate((prev) => (prev ? { ...prev, status: nextStatus } : prev));
      }
      setQueryOpen(false);
      setQueryDetails('');
      success('Query sent', "Thank you. We'll review your message and get back to you.");
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.message || err.message || 'Something went wrong';
      toastError('Could not send query', msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectDialogOpenChange = (open) => {
    if (!open && actionLoading === 'reject') return;
    setRejectOpen(open);
    if (!open) {
      setRejectTags(new Set());
      setRejectComment('');
    }
  };

  const handleQueryDialogOpenChange = (open) => {
    if (!open && actionLoading === 'query') return;
    setQueryOpen(open);
    if (!open) {
      setQueryReason('price');
      setQueryDetails('');
    }
  };

  if (isMaintenanceMode) {
    return <Maintenance />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--ds-canvas-bg)] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
          <p className="mt-2 text-meta text-[var(--ds-text-muted)]">Loading estimate...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--ds-canvas-bg)] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            <div className="text-[var(--ds-status-danger)] text-5xl mb-4">⚠️</div>
            <h1 className="text-h1 font-bold text-[var(--ds-text-primary)] mb-2">Estimate Not Found</h1>
            <p className="text-body text-[var(--ds-text-secondary)] mb-4">{error}</p>
            <p className="text-meta text-[var(--ds-text-muted)]">
              This link may have expired or the estimate may have been removed.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!estimate) {
    return null;
  }

  const portalSlugForEntry =
    estimate.tenant?.portalSlug?.trim() || '';

  const showRespondActions = estimate.status === 'sent';
  const isPastExpiry =
    Boolean(estimate.expiryDate) &&
    estimate.status === 'sent' &&
    new Date(estimate.expiryDate) < new Date(new Date().toDateString());

  return (
    <div className="min-h-screen bg-[var(--ds-canvas-bg)] py-8">
      <ToastContainer />
      <div className="max-w-5xl mx-auto px-6">
        {/* Header */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h1 className="text-display font-bold text-[var(--ds-text-primary)]">Estimate</h1>
                <p className="text-meta text-[var(--ds-text-secondary)] mt-1">
                  From: {estimate.tenant?.name || 'SparxMate'}
                </p>
                <div className="mt-3">
                  <Link
                    to={`/client/portal-entry?token=${encodeURIComponent(token)}${
                      portalSlugForEntry ? `&portalSlug=${encodeURIComponent(portalSlugForEntry)}` : ''
                    }`}
                    className={cn(
                      'inline-flex h-8 items-center justify-center rounded-md border border-[var(--ds-control-field-border)]',
                      'bg-[var(--ds-surface-panel2)] px-3 text-xs font-medium text-[var(--ds-text-primary)] shadow-sm',
                      'hover:bg-[var(--ds-control-field-hover-bg)] no-underline'
                    )}
                  >
                    Go to Client Portal
                  </Link>
                </div>
              </div>
              {showRespondActions && (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  {isPastExpiry && (
                    <p className="text-meta text-[var(--ds-status-warning)] max-w-md">
                      This quote is past its expiry date; you can still accept or decline if you wish.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="default"
                      disabled={!!actionLoading}
                      onClick={handleAccept}
                    >
                      {actionLoading === 'accept' ? 'Accepting…' : 'Accept estimate'}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!!actionLoading}
                      onClick={() => setQueryOpen(true)}
                    >
                      Query / alter estimate
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={!!actionLoading}
                      onClick={() => setRejectOpen(true)}
                    >
                      Decline
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Estimate Details */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Estimate Info */}
              <div>
                <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">Estimate Details</h2>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-meta text-[var(--ds-text-muted)]">Estimate Number:</dt>
                    <dd className="text-meta font-medium text-[var(--ds-text-primary)]">{estimate.estimateNum}</dd>
                  </div>
                  {estimate.reference && (
                    <div className="flex justify-between">
                      <dt className="text-meta text-[var(--ds-text-muted)]">Reference:</dt>
                      <dd className="text-meta font-medium text-[var(--ds-text-primary)]">{estimate.reference}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-meta text-[var(--ds-text-muted)]">Date Created:</dt>
                    <dd className="text-meta font-medium text-[var(--ds-text-primary)]">
                      {formatDate(estimate.dateCreated)}
                    </dd>
                  </div>
                  {estimate.dateIssued && (
                    <div className="flex justify-between">
                      <dt className="text-meta text-[var(--ds-text-muted)]">Date Issued:</dt>
                      <dd className="text-meta font-medium text-[var(--ds-text-primary)]">{formatDate(estimate.dateIssued)}</dd>
                    </div>
                  )}
                  {estimate.expiryDate && (
                    <div className="flex justify-between">
                      <dt className="text-meta text-[var(--ds-text-muted)]">Expiry Date:</dt>
                      <dd className="text-meta font-medium text-[var(--ds-text-primary)]">{formatDate(estimate.expiryDate)}</dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-meta text-[var(--ds-text-muted)]">Status:</dt>
                    <dd className="text-meta">
                      <Badge variant={getStatusVariant(estimate.status)}>
                        {estimate.status.replace('-', ' ').toUpperCase()}
                      </Badge>
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Bill To */}
              <div>
                <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">Estimate For:</h2>
                <div className="text-meta text-[var(--ds-text-primary)]">
                  <p className="font-medium">{getClientName()}</p>
                  {estimate.client.addressLine1 && <p>{estimate.client.addressLine1}</p>}
                  {estimate.client.addressLine2 && <p>{estimate.client.addressLine2}</p>}
                  {(estimate.client.city || estimate.client.postcode) && (
                    <p>{[estimate.client.city, estimate.client.postcode].filter(Boolean).join(', ')}</p>
                  )}
                  {estimate.client.county && <p>{estimate.client.county}</p>}
                  {estimate.client.email && <p className="mt-2">{estimate.client.email}</p>}
                  {estimate.client.phone && <p>{estimate.client.phone}</p>}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Line Items */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">Line Items</h2>

            {estimate.materials && estimate.materials.length > 0 && (
              <div className="mb-6">
                <h3 className="text-meta font-medium text-[var(--ds-text-secondary)] mb-3 bg-[var(--ds-control-field-bg)] px-3 py-2 rounded-[var(--ds-radius-md)]">Materials</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-[var(--ds-border-divider)]">
                        <th className="px-4 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase">Description</th>
                        <th className="px-4 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase">Part Number</th>
                        <th className="px-4 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase">Qty</th>
                        <th className="px-4 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase">Unit Price</th>
                        <th className="px-4 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estimate.materials.map((item) => (
                        <tr key={item.id} className="border-b border-[var(--ds-border-divider)]">
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)]">{item.description}</td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-muted)]">{item.partNumber || '-'}</td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)] text-right">{item.quantity}</td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)] text-right">{formatCurrency(item.unitPrice)}</td>
                          <td className="px-4 py-3 text-body font-medium text-[var(--ds-text-primary)] text-right">{formatCurrency(item.lineTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {estimate.labour && estimate.labour.length > 0 && (
              <div>
                <h3 className="text-meta font-medium text-[var(--ds-text-secondary)] mb-3 bg-[var(--ds-control-field-bg)] px-3 py-2 rounded-[var(--ds-radius-md)]">Labour</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead>
                      <tr className="border-b border-[var(--ds-border-divider)]">
                        <th className="px-4 py-3 text-left text-meta text-[var(--ds-text-muted)] uppercase">Description</th>
                        <th className="px-4 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase">Hours</th>
                        <th className="px-4 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase">Hourly Rate</th>
                        <th className="px-4 py-3 text-right text-meta text-[var(--ds-text-muted)] uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {estimate.labour.map((item) => (
                        <tr key={item.id} className="border-b border-[var(--ds-border-divider)]">
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)]">{item.description}</td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)] text-right">{item.hours}</td>
                          <td className="px-4 py-3 text-body text-[var(--ds-text-primary)] text-right">{formatCurrency(item.hourlyRate)}</td>
                          <td className="px-4 py-3 text-body font-medium text-[var(--ds-text-primary)] text-right">{formatCurrency(item.lineTotal)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="flex justify-end">
              <div className="w-full md:w-64 space-y-2">
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)]">Materials Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(estimate.materialsSubtotal)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)]">Labour Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(estimate.labourSubtotal)}</span>
                </div>
                <div className="flex justify-between text-body border-t border-[var(--ds-border-divider)] pt-2">
                  <span className="text-[var(--ds-text-muted)]">Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(estimate.subtotal)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)]">VAT ({estimate.vatRate}%):</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(estimate.vatAmount)}</span>
                </div>
                <div className="flex justify-between text-h2 font-bold border-t border-[var(--ds-border-divider)] pt-2">
                  <span className="text-[var(--ds-text-primary)]">Total:</span>
                  <span className="text-[var(--ds-text-primary)]">{formatCurrency(estimate.total)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes & Terms */}
        {(estimate.notes || estimate.terms) && (
          <Card>
            <CardContent className="p-6">
              {estimate.notes && (
                <div className="mb-4">
                  <h3 className="text-meta font-medium text-[var(--ds-text-secondary)] mb-2">Notes:</h3>
                  <div
                    className="text-meta text-[var(--ds-text-secondary)] [&_a]:text-[var(--ds-accent-primary)] [&_a]:underline"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichTextHtml(estimate.notes),
                    }}
                  />
                </div>
              )}
              {estimate.terms && (
                <div>
                  <h3 className="text-meta font-medium text-[var(--ds-text-secondary)] mb-2">Terms & Conditions:</h3>
                  <div
                    className="text-meta text-[var(--ds-text-secondary)] [&_a]:text-[var(--ds-accent-primary)] [&_a]:underline"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeRichTextHtml(estimate.terms),
                    }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center text-meta text-[var(--ds-text-muted)]">
          <p>This estimate was generated by {estimate.tenant?.name || 'SparxMate'}</p>
          <p className="mt-1">Thank you for your interest!</p>
        </div>

        <Dialog.Root open={rejectOpen} onOpenChange={handleRejectDialogOpenChange}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100]" />
            <Dialog.Content className="fixed top-1/2 left-1/2 z-[101] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] shadow-[var(--ds-shadow-popover)]">
              <div className="flex items-start justify-between gap-3 border-b border-[var(--ds-border-divider)] px-5 py-4">
                <Dialog.Title className="text-h2 font-semibold text-[var(--ds-text-primary)] pr-2">
                  Decline this estimate
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="rounded-md p-1.5 text-[var(--ds-text-muted)] hover:bg-[var(--ds-control-field-hover-bg)] hover:text-[var(--ds-text-primary)]"
                    aria-label="Close"
                    disabled={!!actionLoading}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
              <div className="space-y-4 px-5 py-4">
                <p className="text-meta text-[var(--ds-text-secondary)]">
                  Please can you provide more information on why you rejected the estimate?
                </p>
                <div>
                  <FormLabel className="mb-2">Quick options</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(PORTAL_REJECT_TAG_LABELS).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => toggleRejectTag(key)}
                        className={cn(
                          'rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          rejectTags.has(key)
                            ? 'border-[var(--ds-accent-primary)] bg-[var(--ds-accent-primary)] text-white'
                            : 'border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] text-[var(--ds-text-primary)] hover:bg-[var(--ds-control-field-hover-bg)]'
                        )}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <FormLabel htmlFor="reject-comment" className="mb-2">
                    Further details (optional)
                  </FormLabel>
                  <Textarea
                    id="reject-comment"
                    value={rejectComment}
                    onChange={(e) => setRejectComment(e.target.value)}
                    rows={4}
                    placeholder="Anything else that would help?"
                    className="min-h-[100px] text-sm"
                    disabled={!!actionLoading}
                  />
                </div>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!!actionLoading}
                    onClick={() => handleRejectDialogOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    disabled={!!actionLoading}
                    onClick={submitReject}
                  >
                    {actionLoading === 'reject' ? 'Submitting…' : 'Submit decline'}
                  </Button>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        <Dialog.Root open={queryOpen} onOpenChange={handleQueryDialogOpenChange}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100]" />
            <Dialog.Content className="fixed top-1/2 left-1/2 z-[101] w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] shadow-[var(--ds-shadow-popover)]">
              <div className="flex items-start justify-between gap-3 border-b border-[var(--ds-border-divider)] px-5 py-4">
                <Dialog.Title className="text-h2 font-semibold text-[var(--ds-text-primary)] pr-2">
                  Query or alter this estimate
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    className="rounded-md p-1.5 text-[var(--ds-text-muted)] hover:bg-[var(--ds-control-field-hover-bg)] hover:text-[var(--ds-text-primary)]"
                    aria-label="Close"
                    disabled={!!actionLoading}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </Dialog.Close>
              </div>
              <div className="space-y-4 px-5 py-4">
                <p className="text-meta text-[var(--ds-text-secondary)]">
                  Tell us what you would like changed. Your contractor will see this message.
                </p>
                <div>
                  <FormLabel htmlFor="query-reason" className="mb-2">
                    Reason
                  </FormLabel>
                  <select
                    id="query-reason"
                    value={queryReason}
                    onChange={(e) => setQueryReason(e.target.value)}
                    disabled={!!actionLoading}
                    className={cn(
                      'flex h-10 w-full rounded-md border border-[var(--ds-control-field-border)] bg-[var(--ds-control-field-bg)] px-3 text-sm',
                      'text-[var(--ds-text-primary)]'
                    )}
                  >
                    {Object.entries(PORTAL_QUERY_REASON_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FormLabel htmlFor="query-details" className="mb-2">
                    Further information
                  </FormLabel>
                  <Textarea
                    id="query-details"
                    value={queryDetails}
                    onChange={(e) => setQueryDetails(e.target.value)}
                    rows={4}
                    placeholder="Describe the changes or questions you have…"
                    className="min-h-[100px] text-sm"
                    disabled={!!actionLoading}
                  />
                </div>
                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={!!actionLoading}
                    onClick={() => handleQueryDialogOpenChange(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    disabled={!!actionLoading}
                    onClick={submitQuery}
                  >
                    {actionLoading === 'query' ? 'Sending…' : 'Send query'}
                  </Button>
                </div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>
    </div>
  );
};

export default PublicEstimateView;

